import * as Sentry from '@sentry/react';
import { generatePath, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import Modal from 'components/modal/Modal';
import { CardHeader, CardHeaderTitle } from 'components/card/card-header';
import { Form, Formik } from 'formik';
import { CardFooter } from 'components/card';
import FieldInput from 'components/input/FieldInput';
import messages from 'messages';
import { Button, ButtonsGroup } from 'components/button';
import {
  MUTATION_ADD_PERSONS_TO_PROGRAM,
  MUTATION_ADD_PROGRAM,
  QUERY_GET_PROGRAMS
} from 'services/aws/programs-query';
import ListBody from 'components/list/ListBody';
import { ListItemLabel, ListItemLabelWrapper } from 'components/list/list-item';
import { NoteTypes } from 'constants.js';
import { MUTATION_ADD_NOTE } from 'services/aws/notes-query';
import { FieldDatePicker } from 'components/input/FieldDatePicker';
import { useNotificationQueue } from 'components/notification';
import useTemplate from 'hooks/queries/useTemplate';
import Loader from 'components/loader/Loader';
import { parseDateString } from 'utils/date';
import { format } from 'date-fns';
import { useStore } from 'stores/Store';
import { useEffect, useState } from 'react';
import RefactoredCardBody from 'components/card/RefactoredCardBody';
import { RefactoredList } from 'components/list';
import RefactoredListItem from 'components/list/list-item/RefactoredListItem';

const getNote = (exercise, notes) => {
  if (notes && notes.length > 0) {
    return notes[0].note;
  }
  if (exercise.note) {
    return exercise.note;
  }
  return '';
};

function AddProgramModal({
  entityId,
  destiny,
  program,
  persons,
  onClose,
  fromTemplate
}) {
  const { push } = useHistory();
  const intl = useIntl();
  const notification = useNotificationQueue();
  const [addProgram] = useMutation(MUTATION_ADD_PROGRAM);
  const [addNote] = useMutation(MUTATION_ADD_NOTE);
  const [addPersonsToProgram] = useMutation(MUTATION_ADD_PERSONS_TO_PROGRAM);
  const { fetchTemplate, loading } = useTemplate();
  const template = useStore(state => state.template);
  const [exercises, setExercises] = useState([]);

  useEffect(() => {
    const getTemplate = async () => {
      const template = await fetchTemplate({
        variables: { templateId: program?.templateId }
      });
      if (template?.exercises) {
        setExercises(template.exercises);
      }
    };
    if (program?.templateId) {
      getTemplate();
    }
  }, [program?.templateId]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(intl.formatMessage(messages.titleRequired)),
    startdate: Yup.date()
      .transform(parseDateString)
      .typeError(intl.formatMessage(messages.dateRequired))
      .required(intl.formatMessage(messages.dateRequired)),
    duration: Yup.number().required(
      intl.formatMessage(messages.durationRequired)
    )
  });

  const onSubmitForm = async program => {
    let newProgramId = '';
    addProgram({
      variables: {
        ...program,
        startdate: format(program.startdate, 'yyyy-LL-dd')
      },
      refetchQueries: [{ query: QUERY_GET_PROGRAMS, variables: { entityId } }]
    })
      .then(res => {
        newProgramId = res.data.addExerciseProgram.id;
        Promise.all([
          template?.notes &&
            template.notes.length > 0 && {
              ...Object.values(template.notes).map(note => {
                return addNote({
                  variables: {
                    linkId: newProgramId,
                    linkId2: note[0].linkId2,
                    note: note[0].note,
                    noteType: NoteTypes.PROGRAM
                  }
                });
              })
            },
          persons &&
            addPersonsToProgram({
              variables: {
                exerciseProgramId: newProgramId,
                personIds: persons.map(p => p.id)
              }
            })
        ]).then(() => {
          push(
            generatePath(destiny.path, {
              entityId,
              sessionsTab: 'sessions',
              programId: newProgramId,
              ...destiny.variables
            })
          );
          onClose && onClose();
        });
      })
      .catch(error => {
        notification.add(`addProgramError${program.id}`, {
          message: intl.formatMessage(messages.messageAddProgramSaveError),
          level: 'error'
        });
        Sentry.captureException(error);

        onClose && onClose();
      });
  };

  const onCloseHandler = () => {
    if (onClose) onClose();
  };

  return (
    <Modal card autoHeight isOpen={true} onClose={onCloseHandler}>
      <CardHeader modal>
        <CardHeaderTitle>
          <FormattedMessage
            {...messages.exercisesAddProgramTitle}
            values={{
              fromTemplate
            }}
          />
        </CardHeaderTitle>
      </CardHeader>
      <Formik
        initialValues={program}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={values => onSubmitForm(values)}
      >
        {({ errors, touched, handleChange, isSubmitting }) => {
          return (
            <Form noValidate>
              <RefactoredCardBody>
                <FieldInput
                  id="title"
                  name="title"
                  placeholder={intl.formatMessage(
                    messages.programPlaceholderName
                  )}
                  errors={errors}
                  touched={touched}
                  required
                >
                  <FormattedMessage {...messages.programLabelName} />
                </FieldInput>
                <div className="o-layout o-layout--small">
                  <div className="o-layout__item u-1-of-2">
                    <FieldDatePicker
                      id="startdate"
                      name="startdate"
                      icon="calendar"
                      placeholder={intl.formatMessage(
                        messages.programPlaceholderStartDate
                      )}
                      errors={errors}
                      touched={touched}
                      onChange={handleChange}
                      required
                      yearDropdownItemNumber={2}
                    >
                      <FormattedMessage {...messages.programLabelStartDate} />
                    </FieldDatePicker>
                  </div>
                  <div className="o-layout__item u-1-of-2">
                    <FieldInput
                      id="duration"
                      name="duration"
                      placeholder={intl.formatMessage(
                        messages.programPlaceholderWeeks
                      )}
                      errors={errors}
                      touched={touched}
                      required
                    >
                      <FormattedMessage {...messages.programLabelWeeks} />
                    </FieldInput>
                  </div>
                </div>
              </RefactoredCardBody>
              {fromTemplate && (
                <RefactoredCardBody
                  style={{ paddingTop: 0, overflow: 'hidden' }}
                >
                  {/* TODO: fabian, refactor verder! */}
                  <CardHeaderTitle
                    style={{
                      padding: '16px 24px 8px',
                      boxShadow: '0 0 10px -7px rgba(0, 0, 0, .4)',
                      zIndex: 1,
                      position: 'relative',
                      marginLeft: -24,
                      marginRight: -24
                    }}
                  >
                    <FormattedMessage {...messages.exercisesInTemplate} />
                  </CardHeaderTitle>
                  {loading && <Loader />}
                  <RefactoredList style={{ minHeight: 140 }}>
                    <ListBody style={{ paddingTop: 12 }}>
                      {exercises.map(exercise => (
                        <RefactoredListItem key={exercise.id}>
                          <ListItemLabelWrapper>
                            <ListItemLabel label>
                              {exercise.title}
                            </ListItemLabel>
                            <p className="r-list__item-meta">
                              {getNote(exercise, template.notes[exercise.id])}
                            </p>
                          </ListItemLabelWrapper>
                        </RefactoredListItem>
                      ))}
                    </ListBody>
                  </RefactoredList>
                </RefactoredCardBody>
              )}
              <CardFooter modal extraClassNames="c-card__footer--modal-larger">
                <ButtonsGroup>
                  <Button secondary onClick={() => onCloseHandler()}>
                    <FormattedMessage {...messages.buttonCancel} />
                  </Button>
                  <Button type="submit" primary disabled={isSubmitting}>
                    <FormattedMessage {...messages.buttonSave} />
                  </Button>
                </ButtonsGroup>
              </CardFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

export default AddProgramModal;
